import { useToastContext } from "hooks/useToastContext";
import { useCallback, useEffect, useRef, useState } from "react";
import intl from "react-intl-universal";
import {
  GetLoadByBusinessUnitAndSupplierAsync,
  saveLoadCapacityAsync,
} from "services/load-capacities.service";
import {
  GetUnloadByBusinessUnitAndFamilyAsync,
  GetUnloadByBusinessUnitAndMaterialGroupAsync,
  saveUnloadCapacityAsync,
} from "services/unload-capacities.service";
import type { ICapacityByDayDto } from "types/capacity";
import type { ILoadCapacityByDay, ILoadCapacityDto } from "types/load-capacity";
import type { IUnloadCapacityByDay, IUnloadCapacityDto } from "types/unload-capacity";
import { validatesArrayHasValue } from "utils/custom-functions";
import { formatErrorMessage } from "utils/handle-error";
import { useGridParameterizationContext } from "../../context";
import { gridParameterizationType, isFilterSelected, unloadCapacityRadioValues } from "../../utils";
import GridParameterizationCalendar from "../grid-parameterization-calendar";
import GridParameterizationFilter from "../grid-parameterization-filter";
import GridParameterizationFooter from "../grid-parameterization-footer";
import { Footer, GridParameterizationContentContainer, Main } from "./styled";

interface IGridParameterizationProps {
  type: string;
}

const GridParameterizationContent = ({ type }: IGridParameterizationProps) => {
  const { openToast } = useToastContext();
  const openToastRef = useRef(openToast);
  const {
    startLoading,
    stopLoading,
    filtersLoadCapacity,
    filtersUnloadCapacity,
    selectedRadioUnloadCapacity,
    calendarDateView,
  } = useGridParameterizationContext();
  const [originalData, setOriginalData] = useState<ILoadCapacityByDay[] | IUnloadCapacityByDay[]>(
    []
  );
  const [editedData, setEditedData] = useState<ILoadCapacityByDay[] | IUnloadCapacityByDay[]>([]);
  const [currentMonthHasUnsavedChanges, setCurrentMonthHasUnsavedChanges] =
    useState<boolean>(false);

  useEffect(() => {
    openToastRef.current = openToast;
  }, [openToast]);

  useEffect(() => {
    const hasChanges = JSON.stringify(originalData) !== JSON.stringify(editedData);
    setCurrentMonthHasUnsavedChanges(hasChanges);
  }, [originalData, editedData]);

  const handleClearCalendarData = useCallback(
    (data: ILoadCapacityByDay[] | IUnloadCapacityByDay[] = []) => {
      setOriginalData([...data]);
      setEditedData([...data]);
    },
    []
  );

  const getCalendarDataByType = useCallback(async () => {
    const _startDate = calendarDateView.startDate?.split("T")[0];
    const _endDate = calendarDateView.endDate?.split("T")[0];
    if (type === gridParameterizationType.LoadCapacity) {
      return await GetLoadByBusinessUnitAndSupplierAsync(
        _startDate,
        _endDate,
        filtersLoadCapacity.businessUnitId,
        filtersLoadCapacity.supplierId
      );
    } else if (
      type === gridParameterizationType.UnloadCapacity &&
      selectedRadioUnloadCapacity === "materialGroup"
    ) {
      return await GetUnloadByBusinessUnitAndMaterialGroupAsync(
        _startDate,
        _endDate,
        filtersUnloadCapacity.businessUnitId,
        filtersUnloadCapacity.materialGroupId
      );
    } else if (
      type === gridParameterizationType.UnloadCapacity &&
      selectedRadioUnloadCapacity === "family"
    ) {
      return await GetUnloadByBusinessUnitAndFamilyAsync(
        _startDate,
        _endDate,
        filtersUnloadCapacity.businessUnitId,
        filtersUnloadCapacity.familyId
      );
    } else {
      return Promise.resolve([]);
    }
  }, [
    calendarDateView,
    type,
    filtersLoadCapacity,
    filtersUnloadCapacity,
    selectedRadioUnloadCapacity,
  ]);

  const handleGetCalendarDataAsync = useCallback(async () => {
    try {
      startLoading();
      const calendarData: ILoadCapacityByDay[] | IUnloadCapacityByDay[] =
        await getCalendarDataByType();
      setOriginalData(calendarData);
      setEditedData(calendarData);
    } catch (error) {
      openToastRef.current(formatErrorMessage(error), "negative");
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading, getCalendarDataByType]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation> calendarDateViewNeeded </explanation>
  useEffect(() => {
    if (
      isFilterSelected(
        type,
        filtersLoadCapacity,
        filtersUnloadCapacity,
        selectedRadioUnloadCapacity
      )
    ) {
      handleGetCalendarDataAsync();
    } else {
      handleClearCalendarData();
    }
  }, [
    type,
    filtersLoadCapacity,
    filtersUnloadCapacity,
    selectedRadioUnloadCapacity,
    calendarDateView,
    handleGetCalendarDataAsync,
    handleClearCalendarData,
  ]);

  const handleRemoveCapacityValue = (
    date: string
  ): ILoadCapacityByDay[] | IUnloadCapacityByDay[] => {
    const originalRecord = originalData.find((x) => x.Date === date);

    // Did not exist originally: remove from array
    if (!originalRecord) {
      return editedData.filter((x) => x.Date !== date);
    }

    // Existed but was empty: restore to original state
    if (originalRecord.Capacity === undefined || originalRecord.Capacity === null) {
      return editedData.map((x) => (x.Date === date ? { ...originalRecord } : x));
    }

    // Existed and had value: mark as deleted
    return editedData.map((x) =>
      x.Date === date ? { ...x, Capacity: undefined, Deleted: true } : x
    );
  };

  const handleInputCellChange = (date: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const dateHasValue = editedData.some((x) => x?.Date === date);
    const rawValue = event.target.value?.trim();

    if (!rawValue && dateHasValue) {
      setEditedData(handleRemoveCapacityValue(date));
      return;
    }

    const capacity = Number.parseFloat(rawValue) || 0;

    if (!validatesArrayHasValue(editedData) || !dateHasValue) {
      // Cria novo registro com valores padrão
      const newValue = {
        Date: date,
        Capacity: capacity,
        BusinessUnit: "",
        BusinessUnitId: 0,
        CreationUser: "",
        CreationUserId: 0,
        supplier: "",
        Family: "",
        FamilyId: 0,
        MaterialGroup: "",
        MaterialGroupId: undefined,
        Blocked: undefined,
      };

      setEditedData((prevState) => [...prevState, newValue]);
    } else {
      // Atualiza apenas o capacity do registro existente
      setEditedData((prevState) =>
        prevState.map((x: ICapacityByDayDto) => {
          if (x.Date !== date) {
            return x;
          } else {
            return { ...x, Capacity: capacity, Deleted: false, Blocked: x.Blocked };
          }
        })
      );
    }
  };

  const handleSaveCalendarDataAsync = async () => {
    try {
      startLoading();
      if (type === gridParameterizationType.LoadCapacity) {
        const _capacities: ILoadCapacityDto[] = editedData.map((item) => ({
          BusinessUnit: item.BusinessUnit,
          BusinessUnitId: filtersLoadCapacity.businessUnitId,
          SupplierId: filtersLoadCapacity.supplierId,
          Capacity: item.Capacity ?? 0,
          CreationUser: item.CreationUser,
          CreationUserId: item.CreationUserId,
          Date: item.Date,
          Id: item.Id,
          Deleted: item.Deleted,
          Blocked: item.Blocked,
        }));
        await saveLoadCapacityAsync(_capacities);
      } else if (
        type === gridParameterizationType.UnloadCapacity &&
        selectedRadioUnloadCapacity === unloadCapacityRadioValues.materialGroup
      ) {
        const _capacities: IUnloadCapacityDto[] = editedData.map((item) => ({
          BusinessUnit: item.BusinessUnit,
          BusinessUnitId: filtersUnloadCapacity.businessUnitId,
          MaterialGroupId: filtersUnloadCapacity.materialGroupId,
          Capacity: item.Capacity ?? 0,
          CreationUser: item.CreationUser,
          CreationUserId: item.CreationUserId,
          Date: item.Date,
          Id: item.Id,
          Deleted: item.Deleted,
          Blocked: item.Blocked,
        }));
        await saveUnloadCapacityAsync(_capacities);
      } else if (
        type === gridParameterizationType.UnloadCapacity &&
        selectedRadioUnloadCapacity === unloadCapacityRadioValues.family
      ) {
        const _capacities: IUnloadCapacityDto[] = editedData.map((item) => ({
          BusinessUnit: item.BusinessUnit,
          BusinessUnitId: filtersUnloadCapacity.businessUnitId,
          FamilyId: filtersUnloadCapacity.familyId,
          Capacity: item.Capacity ?? 0,
          CreationUser: item.CreationUser,
          CreationUserId: item.CreationUserId,
          Date: item.Date,
          Id: item.Id,
          Deleted: item.Deleted,
          Blocked: item.Blocked,
        }));
        await saveUnloadCapacityAsync(_capacities);
      }

      openToastRef.current(
        intl.get(
          "master-data.managementUnity.grid.general-warning-messages.all-changes-saved-successfully"
        ),
        "positive"
      );
    } catch {
      openToastRef.current(
        intl.get(
          "master-data.managementUnity.grid.general-warning-messages.unable-to-save-changes"
        ),
        "negative"
      );
    } finally {
      stopLoading();
      handleGetCalendarDataAsync();
    }
  };

  return (
    <GridParameterizationContentContainer>
      <Main>
        <GridParameterizationFilter key={`${type}-filter`} type={type} />
        <GridParameterizationCalendar
          key={`${type}-calendar`}
          type={type}
          capacityByDay={editedData}
          handleInputCellChange={handleInputCellChange}
          disableChangeMonth={currentMonthHasUnsavedChanges}
          handleGetCalendarDataAsync={handleGetCalendarDataAsync}
        />
      </Main>
      <Footer>
        <GridParameterizationFooter
          handleCancel={() => handleClearCalendarData(originalData)}
          handleSave={handleSaveCalendarDataAsync}
          disabledSave={!currentMonthHasUnsavedChanges}
        />
      </Footer>
    </GridParameterizationContentContainer>
  );
};

export default GridParameterizationContent;
